import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Trash2, HardHat, Loader2 } from "lucide-react";

export const CrewManager = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/provider/crew").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const atCapacity = data && data.seats.limit != null && data.seats.used >= data.seats.limit;

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Fill in name, email and password"); return; }
    setBusy(true);
    try {
      await api.post("/provider/crew", form);
      toast.success(`${form.name} added to your crew`);
      setForm({ name: "", email: "", password: "" });
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/provider/crew/${m.id}`);
      toast.success(`${m.name} removed`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return null;
  const { used, limit } = data.seats;

  return (
    <div data-testid="crew-manager" className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-emerald-950 flex items-center gap-2">
          <HardHat className="w-5 h-5 text-emerald-700" /> Crew members
        </h2>
        <Badge data-testid="crew-seat-count" className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-200">
          {used}{limit != null ? ` / ${limit}` : ""} seats used
        </Badge>
      </div>
      <p className="mt-1 text-xs text-stone-400">{data.plan} plan · crew members log in with their own credentials and share this job board.</p>

      <form onSubmit={add} className="mt-4 grid sm:grid-cols-4 gap-2">
        <Input data-testid="crew-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="rounded-xl" />
        <Input data-testid="crew-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="rounded-xl" />
        <Input data-testid="crew-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temp password" className="rounded-xl" />
        <Button data-testid="crew-add-btn" type="submit" disabled={busy || atCapacity} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Add</>}
        </Button>
      </form>
      {atCapacity && (
        <p data-testid="crew-at-capacity" className="mt-2 text-xs text-amber-700">
          You've used all {limit} seats on the {data.plan} plan. Upgrade your Crew tier to add more members.
        </p>
      )}

      <div className="mt-5 space-y-2">
        {data.members.length === 0 && <p className="text-sm text-stone-400">No crew members yet. Add your first field technician above.</p>}
        {data.members.map((m) => (
          <div key={m.id} data-testid={`crew-member-${m.id}`} className="flex items-center justify-between rounded-xl border border-stone-100 bg-stone-50/60 p-3">
            <div>
              <p className="font-semibold text-emerald-950">{m.name}</p>
              <p className="text-xs text-stone-500">{m.email}</p>
            </div>
            <Button data-testid={`crew-remove-${m.id}`} size="sm" variant="outline" onClick={() => remove(m)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
